import React, { memo, useMemo, useState } from 'react';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import { Blok } from '../../interfaces/Blok.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { CTABarSmallBlokProps } from '../CTABarSmall/CTABarSmall';
import { useInstantSearch } from 'react-instantsearch-hooks-web';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import GroupedArticleIndexPageResultsSideBar from './GroupedArticleIndexPageResultsSideBar';
import NoSearchResultsFound from '../NoSearchResultsFound/NoSearchResultsFound';
import GroupedArticleIndexPageResultsMainArea, {
  GroupedArticleSection,
} from './GroupedArticleIndexPageResultsMainArea';
import GroupedArticleIndexPageLoading from './GroupedArticleIndexPageLoading';

export interface GroupedArticleIndexPageResultsBlokProps extends Blok {
  number_of_results_per_page?: number;
  summary_list_title?: string;
  cta_bar: CTABarSmallBlokProps[];
  no_results_found_heading: string;
  no_results_found_body?: string;
  isLoading: boolean;
}

interface GroupedArticleIndexPageResultsProps {
  blok: GroupedArticleIndexPageResultsBlokProps;
}

const GroupedArticleIndexPageResults: React.FC<
  GroupedArticleIndexPageResultsProps
> = ({ blok }) => {
  const {
    summary_list_title,
    cta_bar,
    no_results_found_heading,
    no_results_found_body = undefined,
    isLoading
  } = blok;

  const {
    results: { hits, nbHits },
  } = useInstantSearch();

  const summaryListItems = useMemo<string[]>(() => {
    return hits
      .map((hit) => hit.subcategory_tags)
      .flatMap((subcategory_tags) => subcategory_tags)
      .filter(
        (subcategory, index, self) => index === self.indexOf(subcategory)
      );
  }, [hits]);

  const articleSections = useMemo<GroupedArticleSection[]>(() => {
    return summaryListItems.map((summaryListItem: string) => {
      return {
        title: summaryListItem,
        articles: hits.filter((hit) =>
          hit.subcategory_tags?.includes(summaryListItem)
        ),
      };
    });
  }, [summaryListItems]);

  const [focussedSummaryListItem, setFocussedSummaryListItem] = useState<string | null>(summaryListItems[0]);

  return (
    <div className="relative min-h-full">
      {isLoading && (
        <GroupedArticleIndexPageLoading />
      )}

      {!isLoading && (
        <>
          {/*-- desktop side bar --*/}
          {nbHits > 0 && (
            <div className="hidden pointer-events-none xl:block xl:z-10 xl:absolute xl:top-0 xl:right-0 xl:bottom-0 xl:left-0 x:h-full">
              <div className="relative h-full">
                <Container>
                  <Grid>
                    <Col span={{ default: 3 }}>
                      <GroupedArticleIndexPageResultsSideBar
                        summaryListTitle={summary_list_title}
                        summaryListItems={summaryListItems}
                        focussedSummaryListItem={focussedSummaryListItem}
                        summaryListItemWasClicked={(newFocussedSummaryListItem: string) => setFocussedSummaryListItem(newFocussedSummaryListItem)}
                      />
                    </Col>
                  </Grid>
                </Container>
              </div>
            </div>
          )}

          {/*-- results list --*/}
          <div className="relative">
            {nbHits > 0 && (
              <GroupedArticleIndexPageResultsMainArea
                articleSections={articleSections}
                ctaBar={cta_bar}
                onScroll={() => setFocussedSummaryListItem(null)}
              />
            )}
            {nbHits === 0 && (
              <Container>
                <NoSearchResultsFound
                  heading={no_results_found_heading}
                  body={no_results_found_body}
                />
              </Container>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(
  withBlokWrapper(GroupedArticleIndexPageResults, Theme.LIGHT)
);
